import React, { useMemo, useState } from 'react';
import {
  AreaChart as RechartsAreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { ChartContainer, ChartConfig } from './ChartContainer';
import { ChartWrapper } from './ChartWrapper';
import { ChartTooltipContent } from './ChartTooltip';
import { ChartLegendContent } from './ChartLegend';
import { defaultPalette } from '../../lib/chartPalette';
import { calculateTickConfig } from '../../lib/chartTickConfig';
import { truncateLabel, calculateOptimalLabelLength } from '../../lib/chartLabelUtils';
import { sanitizeForCSSVar } from '../../lib/chartCssUtils';

export interface AreaConfig {
  dataKey: string;
  type?: 'monotone' | 'linear' | 'step' | 'stepBefore' | 'stepAfter';
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  fillOpacity?: number;
  stackId?: string;
}

export interface AreaChartProps {
  data: any[];
  config?: ChartConfig;
  title?: string;
  description?: string;
  xAxisKey: string;
  areas?: AreaConfig[];
  showGrid?: boolean;
  showTooltip?: boolean;
  showLegend?: boolean;
  height?: number;
  className?: string;
  tooltipFormatter?: (value: any) => string;
  xAxisFormatter?: (value: any) => string;
  yAxisFormatter?: (value: any) => string;
  /** Enable expand button to view chart in fullscreen dialog */
  expandable?: boolean;
  /** Custom height for the expanded chart in the dialog */
  expandedHeight?: number;
  /** @internal Set by ChartWrapper to indicate expanded view - shows all ticks with longer labels */
  isExpanded?: boolean;
}

// Helper to auto-generate config and areas from data
const useChartDefaults = (
  data: any[],
  xAxisKey: string,
  providedConfig?: ChartConfig,
  providedAreas?: AreaConfig[]
) => {
  return useMemo(() => {
    if (!data || data.length === 0) {
      return { config: providedConfig || {}, areas: providedAreas || [] };
    }

    // Get all keys from first data item, excluding xAxisKey
    const dataKeys = Object.keys(data[0]).filter((key) => key !== xAxisKey);

    // Use provided areas or auto-generate from data keys
    const areas = providedAreas || dataKeys.map((key) => ({
      dataKey: key,
      type: 'monotone',
      fill: undefined, // Will be set by CSS var
      stroke: undefined, // Will be set by CSS var
      strokeWidth: 2,
      fillOpacity: 0.3,
      stackId: undefined
    } as AreaConfig));

    // Use provided config or auto-generate with default (violet) palette colors
    const config = providedConfig || dataKeys.reduce((acc, key, index) => {
      acc[key] = {
        label: key.charAt(0).toUpperCase() + key.slice(1), // Capitalize first letter
        color: defaultPalette[index % defaultPalette.length],
      };
      return acc;
    }, {} as ChartConfig);

    return { config, areas };
  }, [data, xAxisKey, providedConfig, providedAreas]);
};

export const AreaChart: React.FC<AreaChartProps> = ({
  data,
  config: providedConfig,
  title,
  description,
  xAxisKey,
  areas: providedAreas,
  showGrid = true,
  showTooltip = true,
  showLegend = true,
  height = 350,
  className,
  tooltipFormatter,
  xAxisFormatter,
  yAxisFormatter,
  expandable = true,
  expandedHeight = 500,
  isExpanded = false,
}) => {
  const { config, areas } = useChartDefaults(data, xAxisKey, providedConfig, providedAreas);
  const [hoveredDataKey, setHoveredDataKey] = useState<string | null>(null);

  // Calculate tick label configuration based on data length
  const tickConfig = useMemo(() => {
    // Get base responsive config (handles rotation based on data length)
    const baseConfig = calculateTickConfig(data.length, { top: 5, right: 10, left: 10, bottom: 5 });

    // In expanded view: keep rotation but force showing ALL ticks
    if (isExpanded) {
      const expandedConfig = {
        ...baseConfig,
        interval: 0,  // Force show all ticks (keep rotation from baseConfig)
        margin: {
          ...baseConfig.margin,
          bottom: baseConfig.margin.bottom + 20, // Extra space to prevent legend overlap
        },
      };
      return expandedConfig;
    }

    // Normal view: use responsive logic as-is
    return baseConfig;
  }, [data.length, isExpanded]);

  // Create x-axis formatter that truncates labels and applies custom formatting
  // In expanded view: 20 chars for better readability, otherwise use responsive calculation
  const xAxisFormatterWithTruncation = useMemo(() => {
    const maxLength = isExpanded ? 20 : calculateOptimalLabelLength(data.length);
    return (value: any) => {
      // Apply custom formatter first if provided
      const formattedValue = xAxisFormatter ? xAxisFormatter(value) : value;
      // Then truncate to prevent overlap
      return truncateLabel(formattedValue, maxLength);
    };
  }, [data.length, xAxisFormatter, isExpanded]);

  const chartContent = (
    <ChartContainer config={config}>
      <ResponsiveContainer width="100%" height={height}>
        <RechartsAreaChart data={data} margin={tickConfig.margin}>
          <defs>
            {areas.map((area) => {
              const sanitizedKey = sanitizeForCSSVar(area.dataKey);
              const color = area.fill || `var(--color-${sanitizedKey})`;
              return (
                <linearGradient
                  key={`gradient-${sanitizedKey}`}
                  id={`gradient-${sanitizedKey}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={color} stopOpacity={0.8} />
                  <stop offset="50%" stopColor={color} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={color} stopOpacity={0.05} />
                </linearGradient>
              );
            })}
          </defs>
          {showGrid && (
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="hsl(var(--border))"
              vertical={false}
            />
          )}
          <XAxis
            dataKey={xAxisKey}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={xAxisFormatterWithTruncation}
            className="text-xs"
            angle={tickConfig.angle}
            textAnchor={tickConfig.textAnchor}
            interval={tickConfig.interval}
            height={tickConfig.margin.bottom}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={yAxisFormatter}
            className="text-xs"
          />
          {showTooltip && (
            <Tooltip
              content={
                <ChartTooltipContent
                  valueFormatter={tooltipFormatter}
                  indicator="dot"
                />
              }
              cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeWidth: 1 }}
            />
          )}
          {showLegend && (
            <Legend
              content={
                <ChartLegendContent
                  onMouseEnter={(dataKey) => setHoveredDataKey(dataKey)}
                  onMouseLeave={() => setHoveredDataKey(null)}
                />
              }
            />
          )}
          {areas.map((area) => {
            const sanitizedKey = sanitizeForCSSVar(area.dataKey);
            return (
              <Area
                key={area.dataKey}
                type={area.type || 'monotone'}
                dataKey={area.dataKey}
                fill={`url(#gradient-${sanitizedKey})`}
                stroke={area.stroke || `var(--color-${sanitizedKey})`}
                strokeWidth={area.strokeWidth || 2}
                stackId={area.stackId}
                opacity={hoveredDataKey === null || hoveredDataKey === area.dataKey ? 1 : 0.3}
              />
            );
          })}
        </RechartsAreaChart>
      </ResponsiveContainer>
    </ChartContainer>
  );

  return (
    <ChartWrapper
      title={title}
      description={description}
      className={className}
      expandable={expandable}
      expandedHeight={expandedHeight}
      chartComponent={AreaChart}
      chartProps={{
        data,
        config: providedConfig,
        xAxisKey,
        areas: providedAreas,
        showGrid,
        showTooltip,
        showLegend,
        height,
        tooltipFormatter,
        xAxisFormatter,
        yAxisFormatter,
        // isExpanded will be added by ChartWrapper in expanded view
      }}
    >
      {chartContent}
    </ChartWrapper>
  );
};
